import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  EnvironmentSettings,
  ENVIRONMENT_SETTINGS,
} from '@epicuro-next/platform/environment';

import { AnalyticsService } from '../services/analytics.service';
import { buildRouteName } from '../services/rum-route.util';

/** Name of the marker event emitted for each validation scenario. */
export const RUM_VALIDATION_EVENT_NAME = 'RumValidation';

/** Schema version stamped on validation telemetry so runs can be correlated. */
export const RUM_SCHEMA_VERSION = '1';

/** Endpoint hit by the "failed request" scenario (expected to 4xx/5xx). */
export const RUM_VALIDATION_FAIL_URL = '/api/rum-validation/fail';

/**
 * Lower-environment-only panel that triggers controlled scenarios to generate
 * known RUM telemetry, so the pipeline (long tasks, crash signal, dependencies,
 * navigation timing) can be validated end-to-end before trusting production
 * dashboards.
 *
 * Reached via `/internal/rum-validation`, gated by `rumValidationEnabledGuard`
 * so it never matches in production. Each scenario emits a `RumValidation`
 * marker event with fixed dimensions *and* performs the real action, so the
 * actual collectors fire too.
 */
@Component({
  selector: 'epc-rum-validation',
  standalone: true,
  template: `
    <section class="rum-validation">
      <h1>RUM validation ({{ environment }})</h1>
      <p>Lower-environment only. Each button generates known telemetry.</p>
      <button type="button" (click)="triggerSlowPage()">
        Slow page (~800ms)
      </button>
      <button type="button" (click)="triggerFailedRequest()">
        Failed request (500)
      </button>
      <button type="button" (click)="triggerFreeze()">
        Browser freeze (~1500ms)
      </button>
      <button type="button" (click)="triggerCrash()">Frontend crash</button>
      <button type="button" (click)="triggerUnhandledRejection()">
        Unhandled rejection
      </button>
    </section>
  `,
})
export class RumValidationComponent {
  private analytics = inject(AnalyticsService);

  private http = inject(HttpClient);

  private router = inject(Router, { optional: true });

  private settings = inject<EnvironmentSettings>(ENVIRONMENT_SETTINGS);

  /** Current environment name, shown in the panel header. */
  public get environment(): string {
    return this.settings?.env ?? 'unknown';
  }

  /** Blocks the main thread briefly to produce long-task/slow-render telemetry. */
  public triggerSlowPage(): void {
    this.mark('slowPage');
    this.block(800);
  }

  /** Calls a known failing endpoint to produce failed-dependency telemetry. */
  public triggerFailedRequest(): void {
    this.mark('failedRequest');
    this.http
      .get(RUM_VALIDATION_FAIL_URL)
      .subscribe({ error: () => undefined });
  }

  /** Blocks the main thread ~1.5s to produce a long-task / freeze signal. */
  public triggerFreeze(): void {
    this.mark('freeze');
    this.block(1500);
  }

  /**
   * Throws synchronously so the error is caught by Angular's zone and routed to
   * the global `ErrorHandler` (`RumErrorHandler`) — which forwards it to App
   * Insights as a `FrontendError` and to Sentry. This mirrors how real app
   * errors surface in a zoned Angular app.
   */
  public triggerCrash(): void {
    this.mark('crash');
    throw new Error('RUM_VALIDATION_FRONTEND_CRASH');
  }

  /** Rejects a promise with no handler to produce unhandled-rejection telemetry. */
  public triggerUnhandledRejection(): void {
    this.mark('unhandledRejection');
    Promise.reject(new Error('RUM_VALIDATION_UNHANDLED_REJECTION'));
  }

  /**
   * Emits the fixed-dimension marker event for a scenario.
   * @param scenario
   */
  private mark(scenario: string): void {
    this.analytics.trackEvent({
      name: RUM_VALIDATION_EVENT_NAME,
      properties: {
        validationScenario: 1,
        validationScenarioName: scenario,
        environment: this.environment,
        routeName: buildRouteName(this.router),
        rumSchemaVersion: RUM_SCHEMA_VERSION,
      },
    });
  }

  /**
   * Busy-waits to block the main thread for the given duration. Validation only.
   * @param durationMs
   */
  private block(durationMs: number): void {
    const end = performance.now() + durationMs;
    while (performance.now() < end) {
      // Intentionally block the main thread in lower-environment validation only.
    }
  }
}
